import type { ErrorComponentProps } from "@tanstack/react-router";
import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import "../styles.css";
import { HERO_SIZES, HERO_SRC, HERO_SRCSET } from "@/lib/site";

function NotFoundComponent() {
  return (
    <div
      id="main-content"
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div
      id="main-content"
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "color-scheme", content: "light" },
      { name: "theme-color", content: "#1c2740" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "google-site-verification", content: "WML71mWeu3R4IF_0UVhJsFJrh9PgdL60BCZLu0q4Erw" },
      { title: "Dog Day Renovations Window Installation Experts" },
      {
        name: "description",
        content:
          "Dog Day Renovations delivers expert window replacement with quality craftsmanship, clear communication, and transparent pricing to upgrade your home’s comfort.",
      },
      { name: "author", content: "Dog Day Renovations" },
      { property: "og:title", content: "Dog Day Renovations Window Installation Experts" },
      {
        property: "og:description",
        content:
          "Dog Day Renovations delivers expert window replacement with quality craftsmanship, clear communication, and transparent pricing to upgrade your home’s comfort.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Dog Day Renovations Window Installation Experts" },
      {
        name: "twitter:description",
        content:
          "Dog Day Renovations delivers expert window replacement with quality craftsmanship, clear communication, and transparent pricing to upgrade your home’s comfort.",
      },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1779471077544-Logo_HighRes_(1).webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1779471077544-Logo_HighRes_(1).webp",
      },
    ],
    links: [
      // Production inlines this CSS into the HTML (see vite.config inlineCss).
      // Keep a stylesheet link in dev so SSR still has styles without a full build.
      ...(import.meta.env.DEV ? [{ rel: "stylesheet", href: appCss }] : []),
      { rel: "icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "alternate", type: "text/plain", href: "/llms.txt", title: "LLM information" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        {/* LCP first so fonts do not steal bandwidth. JSX camelCase serializes correctly. */}
        <link
          rel="preload"
          as="image"
          type="image/webp"
          href={HERO_SRC}
          imageSrcSet={HERO_SRCSET}
          imageSizes={HERO_SIZES}
          fetchPriority="high"
        />
        <HeadContent />
        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-N60XZ24G1S" />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-N60XZ24G1S');`,
          }}
        />
      </head>
      <body>
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return <Outlet />;
}
